import { appendFile, readFile, rename, writeFile } from 'node:fs/promises';

export type LamportTimestamp = { readonly timestamp: number, readonly instanceId: string }

export class LamportClock {
    static #FLUSH_INTERVAL = 5000; // 5 seconds
    static #CLOCK_FILE = './lamport.clock';
    static #WAL_FILE = './lamport.wal';
    #timestamp: number;
    #instanceId: string;
    #flushIntervalId: ReturnType<typeof setInterval>;
    #pendingWrites = 0;

    constructor(instanceId: string) {
        this.#instanceId = instanceId;
        this.#timestamp = 0;
        this.#flushIntervalId = setInterval(() => {
            this.#flush().catch(err => {/* ignore */});
        }, LamportClock.#FLUSH_INTERVAL);
    }

    async init(gossipTimestamp = 0) {
        const baseTimestamp = await this.#loadSnapshot();
        const journaledIncrements = await this.#replayWal();
        this.#timestamp = Math.max(baseTimestamp + journaledIncrements, gossipTimestamp);
    }

    tick(): LamportTimestamp {
        this.#timestamp += 1;
        this.#pendingWrites += 1;
        this.#appendToWal().catch((_err) => {/* ignore */
        }); // async fire-and-forget, no need to await
        return { timestamp: this.#timestamp, instanceId: this.#instanceId };
    }

    observe(gossipTimestamp: number): LamportTimestamp {
        this.#timestamp = Math.max(this.#timestamp, gossipTimestamp) + 1;
        this.#pendingWrites += 1;
        this.#appendToWal().catch((_err) => {/* ignore */
        }); // async fire-and-forget, no need to await
        return { timestamp: this.#timestamp, instanceId: this.#instanceId };
    }

    timestamp(): LamportTimestamp {
        return { timestamp: this.#timestamp, instanceId: this.#instanceId };
    }

    async shutdown(): Promise<void> {
        clearInterval(this.#flushIntervalId);
        await this.#flush();
    }

    async #appendToWal(): Promise<void> {
        const data = `${this.#timestamp}\n`;
        await appendFile(LamportClock.#WAL_FILE, data, 'utf-8');
    }

    async #replayWal() {
        try {
            const data = await readFile(LamportClock.#WAL_FILE, 'utf-8');
            return data.trim().split('\n').filter(Boolean).length;
        }
        catch {
            // ignore
        }
        return 0;
    }

    async #loadSnapshot() {
        try {
            const data = await readFile(LamportClock.#CLOCK_FILE, 'utf-8');
            return parseInt(data, 10) || 0;
        }
        catch {
            // ignore
        }
        return 0;
    }

    async #flush() {
        if (this.#pendingWrites === 0) {
            return;
        }
        const tmp = `${LamportClock.#CLOCK_FILE}.tmp`;
        const data = `${this.#timestamp}\n`;
        await writeFile(tmp, data, { flag: 'w' });
        await rename(tmp, LamportClock.#CLOCK_FILE);
        await writeFile(LamportClock.#WAL_FILE, '', { flag: 'w' }); // truncate WAL
        this.#pendingWrites = 0;
    }
}
